import { Check, ChevronDown, Search } from 'lucide-react'
import type * as React from 'react'
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

export interface SelectOption<T extends string = string> {
  value: T
  label: React.ReactNode
  /** Plain text used for type-ahead/search when `label` isn't a string */
  textValue?: string
  /** Secondary line under the label (e.g. a book's author); also searched when it's text */
  description?: React.ReactNode
  /** Element before the label (e.g. a cover thumbnail) */
  leading?: React.ReactNode
  disabled?: boolean
}

interface SelectProps<T extends string> {
  value: T | null | undefined
  onChange: (value: T) => void
  options: SelectOption<T>[]
  placeholder?: React.ReactNode
  id?: string
  name?: string
  disabled?: boolean
  size?: 'sm' | 'md'
  className?: string
  /** Extra classes for the floating list */
  menuClassName?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-invalid'?: boolean
  onBlur?: () => void
  /** Shows a search field at the top of the list (for long lists) */
  searchable?: boolean
  searchPlaceholder?: string
  /** Shown when the search matches nothing */
  emptyText?: React.ReactNode
}

const MENU_MAX_HEIGHT = 320
const MENU_GAP = 4

interface MenuPosition {
  top: number
  left: number
  width: number
  placement: 'bottom' | 'top'
}

/**
 * Styled replacement for the native <select>.
 * Keyboard: arrows/Home/End move, Enter/Space select, Esc/Tab close, letters jump.
 * The list renders in a portal with fixed positioning so modals with overflow don't clip it.
 */
export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder,
  id,
  name,
  disabled = false,
  size = 'md',
  className,
  menuClassName,
  onBlur,
  searchable = false,
  searchPlaceholder,
  emptyText,
  ...aria
}: SelectProps<T>) {
  const generatedId = useId()
  const triggerId = id ?? `select-${generatedId}`
  const listId = `${triggerId}-list`

  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const typeahead = useRef({ text: '', timeout: 0 })

  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const [query, setQuery] = useState('')

  const selected = options.find((o) => o.value === value) ?? null

  // Options shown in the list; indexes below (activeIndex, data-index) refer to this array
  const visible = useMemo(() => {
    const needle = normalize(query)
    if (!searchable || !needle) return options
    return options.filter((o) => normalize(searchText(o)).includes(needle))
  }, [options, query, searchable])
  const selectedIndex = visible.findIndex((o) => o.value === value)

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const spaceBelow = window.innerHeight - rect.bottom
    const placement = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow ? 'top' : 'bottom'
    setPosition({
      top: placement === 'bottom' ? rect.bottom + MENU_GAP : rect.top - MENU_GAP,
      left: rect.left,
      width: rect.width,
      placement,
    })
  }, [])

  const open = () => {
    if (disabled) return
    updatePosition()
    setQuery('')
    const current = options.findIndex((o) => o.value === value)
    setActiveIndex(current >= 0 ? current : firstEnabled(options, 0, 1))
    setIsOpen(true)
  }

  const close = useCallback((focusTrigger = true) => {
    setIsOpen(false)
    if (focusTrigger) triggerRef.current?.focus()
  }, [])

  const choose = (index: number) => {
    const option = visible[index]
    if (!option || option.disabled) return
    if (option.value !== value) onChange(option.value)
    close()
  }

  // Keep the list attached to the trigger while the page scrolls or resizes
  useLayoutEffect(() => {
    if (!isOpen) return
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, updatePosition])

  // Focus the search field (or the list) on open; screen readers follow aria-activedescendant
  useEffect(() => {
    if (!isOpen) return
    if (searchable) searchRef.current?.focus()
    else listRef.current?.focus()
  }, [isOpen, searchable])

  // Typing a search highlights the first match
  useEffect(() => {
    if (searchable && query) setActiveIndex(firstEnabled(visible, 0, 1))
  }, [query, searchable, visible])

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return
      close(false)
      onBlur?.()
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [isOpen, close, onBlur])

  // Keep the active option visible
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [isOpen, activeIndex])

  const jumpToTypedText = (key: string) => {
    const state = typeahead.current
    window.clearTimeout(state.timeout)
    state.text += key.toLocaleLowerCase()
    state.timeout = window.setTimeout(() => {
      state.text = ''
    }, 500)

    const start = isOpen ? activeIndex : options.findIndex((o) => o.value === value)
    const ordered = [...options.slice(start + 1), ...options.slice(0, start + 1)]
    const match = ordered.find(
      (o) => !o.disabled && optionText(o).toLocaleLowerCase().startsWith(state.text)
    )
    if (!match) return
    const index = options.indexOf(match)
    if (isOpen) setActiveIndex(index)
    else if (match.value !== value) onChange(match.value)
  }

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault()
      open()
    } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      jumpToTypedText(event.key)
    }
  }

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setActiveIndex((i) => firstEnabled(visible, i + 1, 1, i))
        break
      case 'ArrowUp':
        event.preventDefault()
        setActiveIndex((i) => firstEnabled(visible, i - 1, -1, i))
        break
      case 'Home':
      case 'End':
        // In the search field these move the text cursor
        if (searchable) break
        event.preventDefault()
        setActiveIndex(
          event.key === 'Home'
            ? firstEnabled(visible, 0, 1)
            : firstEnabled(visible, visible.length - 1, -1)
        )
        break
      case 'Enter':
        event.preventDefault()
        choose(activeIndex)
        break
      case ' ':
        if (searchable) break
        event.preventDefault()
        choose(activeIndex)
        break
      case 'Escape':
        // Don't let a surrounding modal close too
        event.preventDefault()
        event.stopPropagation()
        close()
        break
      case 'Tab':
        close(false)
        onBlur?.()
        break
      default:
        if (
          !searchable &&
          event.key.length === 1 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.altKey
        ) {
          jumpToTypedText(event.key)
        }
    }
  }

  return (
    <>
      {/* biome-ignore lint/a11y/useSemanticElements: custom-styled combobox; a native <select> can't be styled */}
      <button
        ref={triggerRef}
        id={triggerId}
        name={name}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listId : undefined}
        aria-label={aria['aria-label']}
        aria-labelledby={aria['aria-labelledby']}
        aria-invalid={aria['aria-invalid']}
        disabled={disabled}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleTriggerKeyDown}
        onBlur={() => {
          if (!isOpen) onBlur?.()
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border border-input bg-card text-left text-foreground transition-colors',
          'hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          isOpen && 'ring-2 ring-ring/30 border-ring',
          aria['aria-invalid'] && 'border-error',
          size === 'sm' ? 'h-9 px-3 text-sm' : 'h-10 px-3 text-sm',
          className
        )}
      >
        <span className={cn('min-w-0 truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen &&
        position &&
        createPortal(
          // Keyboard events bubble here from the search field or the listbox
          // biome-ignore lint/a11y/noStaticElementInteractions: container for the combobox popup
          <div
            ref={panelRef}
            onKeyDown={handleListKeyDown}
            // The panel lives in a portal: keep clicks in it from reaching "click outside"
            // handlers of whatever contains the trigger (dropdown panels, popovers)
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: position.left,
              width: position.width,
              maxHeight: MENU_MAX_HEIGHT,
              ...(position.placement === 'bottom'
                ? { top: position.top }
                : { bottom: window.innerHeight - position.top }),
            }}
            className={cn(
              'z-[60] flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg',
              'animate-in fade-in-0 zoom-in-95',
              menuClassName
            )}
          >
            {searchable && (
              <div className="flex shrink-0 items-center gap-2 border-b border-border px-3">
                <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  aria-controls={listId}
                  aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                  className="h-10 w-full min-w-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            {/* biome-ignore lint/a11y/useSemanticElements: ARIA listbox pattern for the custom select */}
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              tabIndex={-1}
              aria-labelledby={triggerId}
              aria-activedescendant={
                !searchable && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
              }
              className="min-h-0 flex-1 overflow-y-auto p-1 outline-none"
            >
              {visible.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {emptyText ?? '—'}
                </p>
              )}
              {visible.map((option, index) => {
                const isSelected = index === selectedIndex
                return (
                  // Keyboard is handled on the panel via aria-activedescendant (WAI-ARIA pattern)
                  // biome-ignore lint/a11y/useKeyWithClickEvents: see above
                  // biome-ignore lint/a11y/useFocusableInteractive: see above
                  // biome-ignore lint/a11y/useSemanticElements: see above
                  <div
                    key={option.value}
                    id={`${listId}-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={option.disabled}
                    onMouseMove={() => !option.disabled && setActiveIndex(index)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(index)}
                    className={cn(
                      'flex cursor-pointer select-none items-center gap-3 rounded-md px-3 py-2 text-sm text-foreground',
                      index === activeIndex && 'bg-muted',
                      isSelected && 'font-medium',
                      option.disabled && 'cursor-not-allowed opacity-50'
                    )}
                  >
                    {option.leading}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.description && (
                        <span className="block truncate text-xs font-normal text-muted-foreground">
                          {option.description}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
                    )}
                  </div>
                )
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

/** Lowercase without accents, for search */
function normalize(text: string) {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim()
}

function searchText(option: SelectOption): string {
  const description = typeof option.description === 'string' ? option.description : ''
  return `${optionText(option)} ${description}`
}

function optionText(option: SelectOption): string {
  if (option.textValue) return option.textValue
  return typeof option.label === 'string' || typeof option.label === 'number'
    ? String(option.label)
    : ''
}

/** Index of the next enabled option from `start` in `step` direction, or `fallback` */
function firstEnabled(options: SelectOption[], start: number, step: 1 | -1, fallback = -1) {
  for (let i = start; i >= 0 && i < options.length; i += step) {
    if (!options[i].disabled) return i
  }
  return fallback
}
